<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useSubscriptionStore } from "@/stores/useSubscriptionStore";
import { useUserStore }         from "@/stores/useUserStore";
import { usePlanStore }         from "@/stores/usePlanStore";

const subscriptionStore = useSubscriptionStore();
const userStore         = useUserStore();
const planStore         = usePlanStore();

import { useDraggableModal } from "@/composables/useDraggableModal";

const emit = defineEmits(["close", "refresh"]);

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

// ====================================================================
// ESTADO DO FORMULÁRIO
// ====================================================================
const form = ref({
  userId:    null,
  planId:    null,
  status:    "PendingPayment",
  startDate: "",
  endDate:   "",
});

const errors       = ref({});
const isSubmitting = ref(false);

// ====================================================================
// AUTOCOMPLETE — USUÁRIO
// ====================================================================
const userSearch      = ref("");
const userResults     = ref([]);
const userLoading     = ref(false);
const showUserDropdown = ref(false);
const selectedUserLabel = ref("");
let userDebounce = null;

async function onUserInput() {
  form.value.userId = null;
  selectedUserLabel.value = "";
  const q = userSearch.value.trim();
  if (!q) { userResults.value = []; showUserDropdown.value = false; return; }
  userLoading.value = true;
  clearTimeout(userDebounce);
  userDebounce = setTimeout(async () => {
    try {
      await userStore.fetchUsers(1, 10, q);
      userResults.value = userStore.users;
      showUserDropdown.value = userResults.value.length > 0;
    } catch { userResults.value = []; }
    finally { userLoading.value = false; }
  }, 300);
}

function selectUser(user) {
  form.value.userId   = user.userId;
  selectedUserLabel.value = user.fullName || user.email || `#${user.userId}`;
  userSearch.value    = selectedUserLabel.value;
  showUserDropdown.value = false;
  userResults.value   = [];
}

function clearUser() {
  form.value.userId = null;
  userSearch.value  = "";
  selectedUserLabel.value = "";
  userResults.value = [];
  showUserDropdown.value = false;
}

// ====================================================================
// AUTOCOMPLETE — PLANO
// ====================================================================
const planSearch       = ref("");
const planResults      = ref([]);
const planLoading      = ref(false);
const showPlanDropdown = ref(false);
const selectedPlanLabel = ref("");
let planDebounce = null;

async function onPlanInput() {
  form.value.planId = null;
  selectedPlanLabel.value = "";
  const q = planSearch.value.trim();
  if (!q) { planResults.value = []; showPlanDropdown.value = false; return; }
  planLoading.value = true;
  clearTimeout(planDebounce);
  planDebounce = setTimeout(async () => {
    try {
      await planStore.fetchPlansPaged(1, 10, q);
      planResults.value = planStore.plans;
      showPlanDropdown.value = planResults.value.length > 0;
    } catch { planResults.value = []; }
    finally { planLoading.value = false; }
  }, 300);
}

function formatBillingCycle(cycle) {
  const map = { Monthly: "Mensal", Yearly: "Anual", OneTime: "Avulso" };
  return map[cycle] ?? cycle ?? "";
}

function selectPlan(plan) {
  form.value.planId = plan.planId;
  const cycle = formatBillingCycle(plan.billingCycle);
  selectedPlanLabel.value = cycle
    ? `${plan.name} · ${cycle}`
    : (plan.name || `#${plan.planId}`);
  planSearch.value = selectedPlanLabel.value;
  showPlanDropdown.value = false;
  planResults.value    = [];
}

function clearPlan() {
  form.value.planId = null;
  planSearch.value  = "";
  selectedPlanLabel.value = "";
  planResults.value = [];
  showPlanDropdown.value = false;
}

// ====================================================================
// FECHAR DROPDOWNS AO CLICAR FORA
// ====================================================================
function handleOutsideClick(e) {
  if (!e.target.closest(".autocomplete-wrap")) {
    showUserDropdown.value = false;
    showPlanDropdown.value = false;
  }
}

// ====================================================================
// TOAST
// ====================================================================
const showToast      = ref(false);
const toastMessage   = ref("");
const toastTimeoutId = ref(null);
const toastType      = ref("error");

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => { showToast.value = false; }, 3000);
}
function showErrorToast(msg) {
  toastType.value = "error"; toastMessage.value = msg;
  showToast.value = true; resetToastTimeout();
}
function showSuccessToast(msg) {
  toastType.value = "success"; toastMessage.value = msg;
  showToast.value = true; resetToastTimeout();
}

// ====================================================================
// LIFECYCLE
// ====================================================================
onMounted(async () => {
  document.addEventListener("keydown", handleEscKey);
  document.addEventListener("mousedown", handleOutsideClick);

  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  form.value.startDate =
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
    `T${pad(now.getHours())}:${pad(now.getMinutes())}`;

  await nextTick();
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);
  document.removeEventListener("mousedown", handleOutsideClick);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  clearTimeout(userDebounce);
  clearTimeout(planDebounce);
});

function handleEscKey(e) {
  if (e.key === "Escape") closeModal();
}

// ====================================================================
// VALIDAÇÃO
// ====================================================================
function validateForm() {
  errors.value = {};

  if (!form.value.userId)
    errors.value.userId = "Selecione um usuário.";

  if (!form.value.planId)
    errors.value.planId = "Selecione um plano.";

  if (!form.value.startDate)
    errors.value.startDate = "A data de início é obrigatória.";

  if (form.value.endDate && form.value.startDate) {
    if (new Date(form.value.endDate) <= new Date(form.value.startDate))
      errors.value.endDate = "A data de término deve ser posterior à data de início.";
  }

  return Object.keys(errors.value).length === 0;
}

// ====================================================================
// SUBMIT
// ====================================================================
async function handleSubmit() {
  if (isSubmitting.value) return;

  if (!validateForm()) {
    showErrorToast("Verifique os campos obrigatórios antes de salvar.");
    return;
  }

  isSubmitting.value = true;

  try {
    const payload = {
      userId:    form.value.userId,
      planId:    form.value.planId,
      status:    form.value.status,
      startDate: form.value.startDate,
      ...(form.value.endDate ? { endDate: form.value.endDate } : {}),
    };

    await subscriptionStore.createSubscription(payload);
    showSuccessToast("Assinatura criada com sucesso.");

    setTimeout(() => {
      emit("refresh");
      emit("close");
      isSubmitting.value = false;
    }, 2400);
  } catch (error) {
    console.error("❌ Erro ao criar assinatura:", error);
    const data = error?.response?.data;
    let msg = "Ocorreu um erro ao criar a assinatura.";
    if (data?.errors) {
      const msgs = Object.values(data.errors).flat().filter(Boolean);
      if (msgs.length) msg = msgs.join("\n");
    } else if (data?.message) {
      msg = data.message;
    } else if (error?.message) {
      msg = error.message;
    }
    showErrorToast(msg);
    isSubmitting.value = false;
  }
}

// ====================================================================
// FECHAR
// ====================================================================
function closeModal() {
  if (!isSubmitting.value) emit("close");
}

// ====================================================================
// OPTIONS
// ====================================================================
const statusOptions = [
  { value: "Active",         label: "Ativo"           },
  { value: "PendingPayment", label: "Aguardando Pag." },
  { value: "Cancelled",      label: "Cancelado"       },
  { value: "Expired",        label: "Expirado"        },
  { value: "Upgraded",       label: "Atualizado"      },
];
</script>

<template>
  <div class="modal-backdrop" @click.self="closeModal">
    <div ref="panelRef" class="modal-panel" :style="panelStyle">

      <!-- HEADER -->
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h2 class="modal-title">Adicionar Assinatura</h2>
        <button @click="closeModal" class="modal-close-btn" :disabled="isSubmitting">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0
                 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10
                 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293
                 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"/>
          </svg>
        </button>
      </div>

      <form @submit.prevent="handleSubmit" novalidate>
        <div class="modal-body">

          <!-- LINHA 1: Usuário + Plano -->
          <div class="form-grid-2">

            <!-- Usuário (autocomplete) -->
            <div class="form-group autocomplete-wrap">
              <label class="form-label">
                Usuário <span class="required-star">*</span>
              </label>
              <div class="autocomplete-input-wrap">
                <input
                  type="text"
                  v-model="userSearch"
                  @input="onUserInput"
                  @focus="showUserDropdown = userResults.length > 0"
                  class="input-base"
                  :class="{ 'is-invalid': errors.userId, 'is-selected': !!form.userId }"
                  placeholder="Buscar por nome ou e-mail…"
                  autocomplete="off"
                />
                <button v-if="form.userId" type="button" class="clear-btn" @click="clearUser">
                  <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
                </button>
                <span v-if="userLoading" class="spinner-inline"/>
              </div>
              <ul v-if="showUserDropdown" class="dropdown-list">
                <li
                  v-for="u in userResults"
                  :key="u.userId"
                  @mousedown.prevent="selectUser(u)"
                  class="dropdown-item"
                >
                  <span class="dropdown-main">{{ u.fullName || u.userName }}</span>
                  <span class="dropdown-sub">{{ u.email }} · #{{ u.userId }}</span>
                </li>
              </ul>
              <span v-if="errors.userId" class="error-msg">{{ errors.userId }}</span>
            </div>

            <!-- Plano (autocomplete) -->
            <div class="form-group autocomplete-wrap">
              <label class="form-label">
                Plano <span class="required-star">*</span>
              </label>
              <div class="autocomplete-input-wrap">
                <input
                  type="text"
                  v-model="planSearch"
                  @input="onPlanInput"
                  @focus="showPlanDropdown = planResults.length > 0"
                  class="input-base"
                  :class="{ 'is-invalid': errors.planId, 'is-selected': !!form.planId }"
                  placeholder="Buscar plano…"
                  autocomplete="off"
                />
                <button v-if="form.planId" type="button" class="clear-btn" @click="clearPlan">
                  <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
                </button>
                <span v-if="planLoading" class="spinner-inline"/>
              </div>
              <ul v-if="showPlanDropdown" class="dropdown-list">
                <li
                  v-for="p in planResults"
                  :key="p.planId"
                  @mousedown.prevent="selectPlan(p)"
                  class="dropdown-item"
                >
                  <span class="dropdown-main">
                    {{ p.name }}
                    <span v-if="p.billingCycle" class="billing-badge">{{ formatBillingCycle(p.billingCycle) }}</span>
                  </span>
                  <span class="dropdown-sub">#{{ p.planId }}</span>
                </li>
              </ul>
              <span v-if="errors.planId" class="error-msg">{{ errors.planId }}</span>
            </div>

          </div>

          <!-- LINHA 2: Status (coluna única) -->
          <div class="form-grid-1" style="margin-top: 1rem;">
            <div class="form-group">
              <label class="form-label">
                Status <span class="required-star">*</span>
              </label>
              <select v-model="form.status" class="input-base select-base">
                <option
                  v-for="opt in statusOptions"
                  :key="opt.value"
                  :value="opt.value"
                >{{ opt.label }}</option>
              </select>
            </div>
          </div>

          <!-- LINHA 3: Data de Início + Data de Término -->
          <div class="form-grid-2" style="margin-top: 1rem;">

            <!-- Data de Início -->
            <div class="form-group">
              <label class="form-label">
                Data de Início <span class="required-star">*</span>
              </label>
              <input
                type="datetime-local"
                v-model="form.startDate"
                class="input-base"
                :class="{ 'is-invalid': errors.startDate }"
              />
              <span v-if="errors.startDate" class="error-msg">{{ errors.startDate }}</span>
            </div>

            <!-- Data de Término (opcional) -->
            <div class="form-group">
              <label class="form-label">
                Data de Término
                <span class="optional-tag">opcional</span>
              </label>
              <input
                type="datetime-local"
                v-model="form.endDate"
                class="input-base"
                :class="{ 'is-invalid': errors.endDate }"
              />
              <span v-if="errors.endDate" class="error-msg">{{ errors.endDate }}</span>
              <span class="hint-msg">Se omitida, calculada pelo ciclo do plano.</span>
            </div>

          </div>

          <!-- Aviso -->
          <div class="info-banner" style="margin-top: 1rem">
            <svg class="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 110 20A10 10 0 0112 2z"/>
            </svg>
            <span>
              Assinaturas criadas aqui ignoram o fluxo do Mercado Pago.
              Use para testes ou ajustes manuais.
            </span>
          </div>

        </div>

        <!-- FOOTER -->
        <div class="modal-footer">
          <button type="button" @click="closeModal" class="btn btn-secondary" :disabled="isSubmitting">
            Cancelar
          </button>
          <button type="submit" class="btn btn-success" :disabled="isSubmitting">
            <svg v-if="isSubmitting" class="w-5 h-5 mr-2 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
            </svg>
            <svg v-else class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd"
                d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                clip-rule="evenodd"/>
            </svg>
            <span>{{ isSubmitting ? "Criando..." : "Adicionar Assinatura" }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- TOAST -->
  <Teleport to="body">
    <transition name="toast-fade">
      <div v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4">
        <div :class="['toast pointer-events-auto', toastType === 'error' ? 'toast-error' : 'toast-success']">
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle"/>
              <path v-if="toastType === 'error'" d="M10 5v6M10 13.5v1.5"
                stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
              <path v-else d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor" stroke-width="2.2"
                stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="toast-content">
            <p class="toast-title">{{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}</p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>
          <button type="button" class="toast-close" @click="showToast = false">&times;</button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.modal-panel {
  max-width: 38rem;
  max-height: calc(100vh - 40px);
}
.modal-panel > form { display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0; overflow: hidden; }

.modal-header {
  flex-shrink: 0;
}

.modal-footer {
  flex-shrink: 0;
}

/* GRIDS */
.form-grid-2 { display: grid; grid-template-columns: 1fr; gap: 1rem; }
@media (min-width: 540px) { .form-grid-2 { grid-template-columns: 1fr 1fr; } }
.form-grid-1 { display: grid; grid-template-columns: 1fr; gap: 1rem; }

.required-star { color: var(--danger); font-weight: 700; }
.optional-tag {
  font-size: 0.72rem; font-weight: 500; color: var(--text-muted);
  background-color: var(--surface-3); border: 1px solid var(--neutral-200);
  border-radius: 4px; padding: 0.05rem 0.4rem;
}

.input-base.is-selected { border-color: #86efac; background-color: #f0fdf4; }

/* SELECT — corrige texto cortado */
.select-base {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%2394a3b8'%3E%3Cpath fill-rule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z' clip-rule='evenodd'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 0.6rem center;
  background-size: 1.1rem; padding-right: 2rem;
  /* ✅ FIX: altura explícita garante que o texto não seja cortado */
  height: 2.375rem;
  display: flex; align-items: center;
}

/* AUTOCOMPLETE */
.autocomplete-wrap { position: relative; }
.autocomplete-input-wrap { position: relative; display: flex; align-items: center; }
.autocomplete-input-wrap .input-base { padding-right: 2rem; }

.clear-btn {
  position: absolute; right: 0.5rem; top: 50%; transform: translateY(-50%);
  background: none; border: none; cursor: pointer; padding: 0.1rem;
  color: var(--text-subtle); display: flex; align-items: center;
}
.clear-btn:hover { color: var(--danger); }
.clear-btn svg { width: 0.9rem; height: 0.9rem; }

.spinner-inline {
  position: absolute; right: 0.5rem; top: 50%; transform: translateY(-50%);
  width: 0.85rem; height: 0.85rem; border: 2px solid var(--neutral-200);
  border-top-color: var(--brand); border-radius: 50%;
  animation: spin 0.6s linear infinite;
}
@keyframes spin { to { transform: translateY(-50%) rotate(360deg); } }

.dropdown-list {
  position: absolute; top: 100%; left: 0; right: 0; z-index: 100;
  background: var(--surface); border: 1px solid var(--neutral-200); border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1); margin-top: 2px;
  max-height: 180px; overflow-y: auto; padding: 0.25rem 0; list-style: none;
}

.dropdown-main { font-size: 0.875rem; color: var(--text-dark); font-weight: 500; display: flex; align-items: center; gap: 0.4rem; }
.billing-badge {
  font-size: 0.68rem; font-weight: 600; color: #7c3aed;
  background-color: var(--brand-soft); border-radius: 4px; padding: 0.05rem 0.35rem;
  letter-spacing: 0.02em;
}
.dropdown-sub  { font-size: 0.75rem; color: var(--text-subtle); }

/* MENSAGENS */
.error-msg { color: var(--danger); font-size: 0.78rem; }
.hint-msg  { color: var(--text-subtle); font-size: 0.75rem; margin-top: 0.1rem; }

/* INFO BANNER */
.info-banner {
  display: flex; align-items: flex-start; gap: 0.5rem;
  padding: 0.625rem 0.875rem; background-color: var(--info-bg);
  border: 1px solid #bfdbfe; border-radius: 8px; font-size: 0.8rem; color: var(--link-hover);
}

</style>